/** Supplier collection — **ADR-0040**, requirements SUP-01, SUP-02 and EXP-14.
 *
 * **One list for both sides.** A supplier's "my requests" and a buyer's are the same rows under the
 * same row-level policy, and each row carries `i_am` so this client never infers a role from
 * whether a field is set. There is no organisation parameter anywhere here, because there is no
 * organisation parameter on the server: the session decides which rows exist.
 */

import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { refusalFrom } from "./refusal";
import { api } from "./client";
import type { Scope2Method, Window } from "./queries";
import type { components } from "./schema";

export type Exchange = components["schemas"]["ExchangeOut"];
export type Submission = components["schemas"]["SubmissionOut"];
export type Agreement = components["schemas"]["AgreementOut"];
/** The closed set of allocation bases, **from the generated schema** - the form is typed against it
 * rather than against a list of its own, so a basis the server adds is a type error here until the
 * form has a label for it (S3-02). */
export type AllocationBasisName = components["schemas"]["AllocationBasis"];
export type AllocationInput = components["schemas"]["AllocationIn"];
export type SupplierScope3Share = components["schemas"]["SupplierScope3ShareOut"];
export type RaiseRequestInput = components["schemas"]["ExchangeIn"];
export type GrantAgreementInput = components["schemas"]["AgreementIn"];

const keys = {
  requests: () => ["supplier-exchange", "requests"] as const,
  agreements: () => ["supplier-exchange", "agreements"] as const,
};

/** Every exchange this organisation is a party to, from whichever side. */
export function useExchange(enabled = true) {
  return useQuery({
    queryKey: keys.requests(),
    enabled,
    queryFn: async (): Promise<Exchange[]> => {
      const { data, error, response } = await api.GET("/api/v1/supplier-exchange/requests", {});
      if (error) throw refusalFrom(response, error, "The supplier exchange could not be read.");
      return data ?? [];
    },
  });
}

/** Every agreement this organisation granted, or that names it as the buyer. */
export function useAgreements(enabled = true) {
  return useQuery({
    queryKey: keys.agreements(),
    enabled,
    queryFn: async (): Promise<Agreement[]> => {
      const { data, error, response } = await api.GET("/api/v1/supplier-exchange/agreements", {});
      // Swallowed, an empty list rendered as *"this supplier has not permitted a copy"* — a
      // statement about the supplier, from a dropped request.
      if (error) throw refusalFrom(response, error, "The sharing agreements could not be read.");
      return data ?? [];
    },
  });
}


/** Ask a supplier for a figure - **ADR-0151**. The buyer is the session's organisation: there is no
 * organisation field in the body or anywhere else in this file, because there is none on the server.
 *
 * Not idempotent, and the caller is told so: a request that did not complete may have been created,
 * and sending the same one again would be two requests to the same supplier for the same figure. The
 * form says to look at the list first rather than offering a retry. */
export function useRaiseRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RaiseRequestInput) => {
      const { data, error, response } = await api.POST("/api/v1/supplier-exchange/requests", {
        body: input,
      });
      if (error || !data) throw refusalFrom(response, error, "That request could not be sent.");
      return data;
    },
    onSuccess: () => {
      // The list, and the responsiveness counts under the same key prefix.
      void queryClient.invalidateQueries({ queryKey: keys.requests() });
    },
  });
}

/** Name a supplier this workspace has asked, in its own words (ADR-0221 option B, an Approver's or
 * an Admin's). The label is the buyer's note: the server keeps it on this workspace's own requests,
 * never shows it to the supplier and never ledgers it. An empty label removes it. */
export function useLabelSupplier(supplierOrgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/supplier-exchange/suppliers/{supplier_org_id}/label",
        { params: { path: { supplier_org_id: supplierOrgId } }, body: { label } },
      );
      if (error || !data) throw refusalFrom(response, error, "That name could not be saved.");
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.requests() });
    },
  });
}

export function useAnswerRequest(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      unit: string;
      stated_basis: string;
      /** A direct figure the supplier measured for this customer... */
      quantity?: string;
      /** ...or a share of their own total, **for the product to divide** (S3-02). Never both: the
       * server refuses a body carrying two answers, and the form can only build one. */
      allocation?: AllocationInput;
    }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/supplier-exchange/requests/{request_id}/submissions",
        { params: { path: { request_id: requestId } }, body: { ...input, supersedes_id: null } },
      );
      if (error || !data) throw refusalFrom(response, error, "That answer could not be submitted.");
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.requests() });
    },
  });
}

/** Accept an answer into this organisation's inventory.
 *
 * **Both arguments are the reader's decisions and neither is defaulted**: the site, because a
 * supplier's figure for "our deliveries" names none, and the agreement, because the copy is made
 * under a permission and it has to be the one the reader looked at.
 */
export function useAcceptAnswer(submissionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      facility_id: string;
      agreement_id: string;
      /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). */
      post_close_reason?: string;
    }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/supplier-exchange/submissions/{submission_id}/accept",
        { params: { path: { submission_id: submissionId } }, body: input },
      );
      if (error || !data) throw refusalFrom(response, error, "That answer could not be accepted.");
      return data;
    },
    onSuccess: () => {
      // The figure landed in the inventory, so every total is stale — not only this list.
      void queryClient.invalidateQueries();
    },
  });
}

/** Permit a buyer - **ADR-0160**. The supplier is the session's organisation: `AgreementIn` has no
 * supplier field, and the server takes it from the principal (SUP-02). Not retried: an agreement that
 * did not complete may exist, and the server refuses a second live one for the same buyer anyway,
 * so the failure sentence says to look at the list. */
export function useGrantAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: GrantAgreementInput) => {
      const { data, error, response } = await api.POST("/api/v1/supplier-exchange/agreements", {
        body: input,
      });
      if (error || !data) throw refusalFrom(response, error, "That permission could not be granted.");
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.agreements() });
    },
  });
}

export function useRevokeAgreement(agreementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reason: string }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/supplier-exchange/agreements/{agreement_id}/revoke",
        { params: { path: { agreement_id: agreementId } }, body: input },
      );
      if (error || !data) throw refusalFrom(response, error, "That permission could not be revoked.");
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.agreements() });
    },
  });
}

/** One supplier's accepted Scope 3 figures against the window's Scope 3 - **summed on the server**.
 *
 * The supplier account page used to read every figure in the window and add them up in the browser,
 * which stops working past the client's load ceiling. This is a read of its own, over the same rows
 * as `GET /inventory` (both of ADR-0030's exclusions, ADR-0029's allocation).
 *
 * Keyed under `["inventory"]` so whatever refreshes the totals - an acceptance invalidates all of it
 * - refreshes this beside them. `latestRequestId` is the request whose accepted answer's grade is
 * wanted, and is part of the key. Not issued without a window (`useInventory` says why).
 */
export function useSupplierScope3Share(
  window: Window | undefined,
  method: Scope2Method,
  supplierId: string,
  latestRequestId: string | undefined,
) {
  return useQuery({
    queryKey: [
      "inventory",
      "supplier-scope3-share",
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
      supplierId,
      latestRequestId ?? "",
    ] as const,
    queryFn: window
      ? async (): Promise<SupplierScope3Share> => {
          const { data, error, response } = await api.GET(
            "/api/v1/supplier-exchange/suppliers/{supplier_org_id}/scope3-share",
            {
              params: {
                path: { supplier_org_id: supplierId },
                query: {
                  period_start: window.period_start,
                  period_end: window.period_end,
                  scope2_method: method,
                  ...(latestRequestId ? { latest_request_id: latestRequestId } : {}),
                },
              },
            },
          );
          if (error || !data) {
            throw refusalFrom(response, error, "This supplier's share of Scope 3 could not be read.");
          }
          return data;
        }
      : skipToken,
  });
}
